"use client";
import React, { useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';

const reasons = [
    {
        color: '#ceff1a',
        icon: (c: string) => (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M6 20l5-7 5 4 7-10" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 8h5v5" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        title: 'Outcome-Focused Strategy',
        desc: "We combine technical SEO, content strategy, consumer psychology, and conversion optimisation to build growth systems — not just rankings reports.",
    },
    {
        color: '#a78bfa',
        icon: (c: string) => (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="4" y="6" width="20" height="16" rx="2.5" stroke={c} strokeWidth="1.8" />
                <path d="M4 11h20" stroke={c} strokeWidth="1.5" />
                <rect x="8" y="15" width="12" height="2" rx="1" fill={c} opacity="0.4" />
                <rect x="8" y="19" width="8" height="2" rx="1" fill={c} opacity="0.25" />
            </svg>
        ),
        title: 'Transparent Reporting',
        desc: "You'll receive transparent reporting and clear communication at every stage, so you always know what's working and why.",
    },
    {
        color: '#38bdf8',
        icon: (c: string) => (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M4 14 Q4 4 14 4 Q24 4 24 14 Q24 24 14 24" stroke={c} strokeWidth="1.8" strokeLinecap="round" />
                <path d="M8 18 Q8 24 14 24" stroke={c} strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="14" cy="14" r="3.5" fill={c} fillOpacity="0.2" stroke={c} strokeWidth="1.5" />
            </svg>
        ),
        title: 'Built For Long-Term Performance',
        desc: "Our strategies are built for sustainable, long-term performance rather than short-term ranking spikes.",
    },
    {
        color: '#fb923c',
        icon: (c: string) => (
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14 4 L16.5 10.5 L23.5 10.5 L18 15 L20 21.5 L14 18 L8 21.5 L10 15 L4.5 10.5 L11.5 10.5 Z" stroke={c} strokeWidth="1.8" fill="none" strokeLinejoin="round" />
            </svg>
        ),
        title: 'Built For The Future Of Search',
        desc: "We build for where search is going — AI Overviews, answer engines, and generative search — not just the current algorithm.",
    },
];

function ReasonCard({ reason, index }: { reason: (typeof reasons)[number]; index: number }) {
    const [flipped, setFlipped] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.07 }}
            onMouseEnter={() => setFlipped(true)}
            onMouseLeave={() => setFlipped(false)}
            className="relative"
            style={{ minHeight: 240, perspective: 1200 }}
        >
            <div
                className="relative w-full h-full transition-transform duration-500 ease-out"
                style={{
                    minHeight: 240,
                    transformStyle: 'preserve-3d',
                    transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                }}
            >
                {/* Front */}
                <div
                    className="absolute inset-0 rounded-2xl p-7 flex flex-col gap-4"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.07)',
                    }}
                >
                    <div
                        style={{
                            width: 52, height: 52, borderRadius: 12,
                            background: `${reason.color}14`,
                            border: `1px solid ${reason.color}33`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        {reason.icon(reason.color)}
                    </div>
                    <h3 style={{ fontFamily: CLASH, color: '#ffffff', fontSize: 18, fontWeight: 700, letterSpacing: '0.02em' }}>
                        {reason.title}
                    </h3>
                    <span
                        className="mt-auto text-[11px] font-semibold tracking-[0.15em] uppercase"
                        style={{ fontFamily: CLASH, color: reason.color }}
                    >
                        Hover to read more
                    </span>
                </div>

                {/* Back */}
                <div
                    className="absolute inset-0 rounded-2xl p-7 flex flex-col justify-center gap-3"
                    style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        background: `${reason.color}12`,
                        border: `1px solid ${reason.color}40`,
                    }}
                >
                    <h3 style={{ fontFamily: CLASH, color: reason.color, fontSize: 16, fontWeight: 700, letterSpacing: '0.02em' }}>
                        {reason.title}
                    </h3>
                    <p style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.7 }}>
                        {reason.desc}
                    </p>
                </div>
            </div>
        </motion.div>
    );
}

export default function SEOWhy() {
    return (
        <section className="relative overflow-hidden py-10 md:py-24 px-4 md:px-16 lg:px-24" style={{ background: '#080808' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,0.04) 1.5px, transparent 1.5px)', backgroundSize: '36px 36px' }} />
            <div className="max-w-[1400px] mx-auto relative z-10">
                <div className="flex flex-col md:flex-row gap-8 md:gap-24 items-start mb-8 md:mb-16">
                    <div className="flex-1 flex flex-col gap-5">
                        <motion.span initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: '#ceff1a', fontFamily: CLASH }}>
                            Why Lead Workz
                        </motion.span>
                        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                            className="text-2xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-none"
                            style={{ fontFamily: CLASH, color: '#ffffff' }}>
                            Why Businesses<br />Choose Lead Workz
                        </motion.h2>
                    </div>
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex-1 flex flex-col gap-4 justify-center">
                        <p style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.55)', fontSize: 16, lineHeight: 1.85 }}>Most SEO companies focus on rankings. We focus on outcomes.</p>
                        <p style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.55)', fontSize: 16, lineHeight: 1.85 }}>We combine technical SEO, content strategy, consumer psychology, and conversion optimisation to create <span style={{ color: '#ceff1a', fontWeight: 600 }}>growth systems that attract the right visitors and turn them into customers.</span></p>
                    </motion.div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {reasons.map((r, i) => (
                        <ReasonCard key={r.title} reason={r} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
